import { api } from '@/lib/api';
import type {
  Company,
  CompanyScope,
  CompanyStats,
  PaginatedCompanies,
  CompanyFilters,
  PaginationParams,
  CreateCompanyDto,
  UpdateCompanyDto,
} from '../types/company.types';

const BASE_URL = '/companies';

/**
 * Service pour les Companies
 */
export const companiesService = {
  /**
   * Récupère la liste paginée des companies avec filtres.
   * @param scope 'mine' (default) = buyers CRM du tenant courant.
   *              'tenants' = tenants BPA (super_admin). 'all' = toutes (super_admin).
   */
  async getAll(
    filters: CompanyFilters = {},
    pagination: Partial<PaginationParams> = {},
    scope?: CompanyScope,
  ): Promise<PaginatedCompanies> {
    const params = new URLSearchParams();

    // Pagination
    params.set('page', String(pagination.page || 1));
    params.set('limit', String(pagination.limit || 20));
    if (pagination.sortBy) params.set('sortBy', pagination.sortBy);
    if (pagination.sortOrder) params.set('sortOrder', pagination.sortOrder);

    // Filtres
    if (filters.search) params.set('search', filters.search);
    if (filters.status) params.set('status', filters.status);
    if (filters.companyType) params.set('companyType', filters.companyType);
    if (filters.city) params.set('city', filters.city);

    // Scope
    if (scope) params.set('scope', scope);

    const response = await api.get<PaginatedCompanies>(
      `${BASE_URL}?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Récupère une company par son ID
   */
  async getById(id: number): Promise<Company> {
    const response = await api.get<Company>(`${BASE_URL}/${id}`);
    return response.data;
  },

  /**
   * Récupère les statistiques CRM d'une company (opportunités liées)
   */
  async getStats(id: number): Promise<CompanyStats> {
    const response = await api.get<CompanyStats>(`${BASE_URL}/${id}/stats`);
    return response.data;
  },

  /**
   * Crée une nouvelle company
   */
  async create(data: CreateCompanyDto): Promise<Company> {
    const response = await api.post<Company>(BASE_URL, data);
    return response.data;
  },

  /**
   * Crée ou récupère une company depuis le SIRET via INSEE API.
   * scope='mine' (défaut) = buyer CRM ; scope='tenants' = tenant BPA (super_admin).
   */
  async createFromSiret(
    siret: string,
    companyType?: string,
    scope?: CompanyScope,
  ): Promise<Company> {
    const params = new URLSearchParams();
    if (scope) params.set('scope', scope);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await api.post<Company>(`${BASE_URL}/from-siret${query}`, {
      siret,
      companyType,
    });
    return response.data;
  },

  /**
   * Recherche une company par SIRET
   */
  async getBySiret(siret: string): Promise<Company | null> {
    const response = await api.get<Company | null>(
      `${BASE_URL}/by-siret/${siret}`
    );
    return response.data;
  },

  /**
   * Met à jour une company
   */
  async update(id: number, data: UpdateCompanyDto): Promise<Company> {
    const response = await api.put<Company>(`${BASE_URL}/${id}`, data);
    return response.data;
  },

  /**
   * Supprime une company (soft delete)
   */
  async delete(id: number): Promise<void> {
    await api.delete(`${BASE_URL}/${id}`);
  },
};
